/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback } from "react";
import { theme } from "@nordcloud/gnui";
import {
  ResourceStatus,
  useGetResourcesLazyQuery,
  OsType,
  GetResourcesQuery,
} from "~/generated/graphql";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { useGetResourceUpdateStatusCounts } from "~/views/resources/hooks";
import { statusDetails } from "~/views/resources/ResourceUpdateStatus/const";
import {
  ResourceUpdateStatus,
  UpdateStatusCardItem,
} from "~/views/resources/ResourceUpdateStatus/types";
import { StatusChartItem } from "../components/types";
import { THEME_COLOR_INDIGO_HEX, THEME_COLOR_PURPLE_HEX } from "../const";
import { useFetchAllPages, REQUEST_LIMIT } from "./useFetchAllPages";

type ResourceListItem = {
  id: string;
  osType: OsType | undefined;
  hasAccess: boolean;
};

export function useDashboardResources() {
  const [getData, { data, loading: firstPageLoading, fetchMore, error }] =
    useGetResourcesLazyQuery();

  const {
    data: updateStatusCountsData,
    loading: updateStatusCountsLoading,
    error: updateStatusCountsError,
    refetch: refetchUpdateStatusCounts,
  } = useGetResourceUpdateStatusCounts();

  const getResources = useCallback(
    () =>
      getData({
        variables: {
          page: 1,
          limit: REQUEST_LIMIT,
        },
      }),
    [getData]
  );

  const refetch = useCallback(() => {
    getResources();
    refetchUpdateStatusCounts();
  }, [getResources, refetchUpdateStatusCounts]);

  const { items, loading } = useFetchAllPages<
    ResourceListItem,
    GetResourcesQuery
  >({
    firstPageLoading,
    firstPageResult: data,
    pageInfo: data?.resources?.pageInfo,
    fetchMore,
    mapFunc: extractQueryResult,
  });

  return {
    refetch,
    resourcesStatus: getResourcesStatus(items),
    resourcesOsList: getOsList(items),
    loading,
    error,
    count: data?.resources?.pageInfo.count,
    lastUpdateStatus: {
      data: getUpdateStatus(updateStatusCountsData),
      loading: updateStatusCountsLoading,
      error: updateStatusCountsError,
    },
  } as const;
}

const extractQueryResult = (
  data: GetResourcesQuery | undefined
): ResourceListItem[] => {
  return (data?.resources?.result ?? [])
    .filter(isNotNil)
    .map(({ id, os, state }) => ({
      id,
      osType: os?.type ?? undefined,
      hasAccess: state?.status === ResourceStatus.Online,
    }));
};

const getResourcesStatus = (list: ResourceListItem[]): StatusChartItem[] => {
  return [
    {
      label: "Lost Connection",
      count: list.filter(({ hasAccess }) => !hasAccess).length,
      color: theme.color.background.error,
      link: ROUTES.resources.index + "?resourcePingStatuses[]=ConnectionLost",
    },
    {
      label: "Online",
      count: list.filter(({ hasAccess }) => hasAccess).length,
      color: theme.color.background.success,
      link: ROUTES.resources.index + "?resourcePingStatuses[]=Online",
    },
  ];
};

const OS_COLOR = {
  [OsType.Linux]: THEME_COLOR_INDIGO_HEX,
  [OsType.Windows]: THEME_COLOR_PURPLE_HEX,
};

const getOsList = (list: ResourceListItem[]): StatusChartItem[] => {
  return Object.values(OsType).map((osType) => ({
    label: osType,
    count: list.filter((item) => item.osType === osType).length,
    color: OS_COLOR[osType],
  }));
};

const buildUpdateStatusLink = (status: ResourceUpdateStatus): string => {
  return ROUTES.resources.index + `?updateStatus[]=${status}`;
};

const UPDATE_STATUS_ORDER: ResourceUpdateStatus[] = [
  ResourceUpdateStatus.NoPendingUpdates,
  ResourceUpdateStatus.NoUpdatesData,
  ResourceUpdateStatus.Failed,
  ResourceUpdateStatus.PendingUpdates,
  ResourceUpdateStatus.InProgress,
];

const getUpdateStatus = (list: UpdateStatusCardItem[]): StatusChartItem[] =>
  UPDATE_STATUS_ORDER.map((status) => {
    const { label, color } = statusDetails[status];
    return {
      label,
      count: list.find((item) => item.status === status)?.count ?? 0,
      color,
      link: buildUpdateStatusLink(status),
    };
  });
